import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { ApplicationState } from './shared/state/application-state.service';
import { SidenavComponent } from './shared/feature/sidenav/sidenav.component';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, SidenavComponent],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  private readonly _destroyed$ = new Subject<void>();

  isIframe = false;
  collapsed = false;

  sidenavWidth = '256px';
  showHeader = false;

  authService = inject(MsalService);
  msalBroadcastService = inject(MsalBroadcastService);
  applicationStateService = inject(ApplicationState);
  router = inject(Router);

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.showHeader = window.innerWidth <= 768;
  }

  constructor() {}

  ngOnInit(): void {
    this.authService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener;

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroyed$),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        // this prevents the redirect to MS login page automatically
        if (this.authService.instance.getActiveAccount() && this.router.url === '/login') {
            this.router.navigate(['/']);
          }
      });

    this.showHeader = window.innerWidth <= 768;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
